import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
  CollapsibleCard,
  FormField,
  Input,
  Textarea,
  cn,
} from '@insolech/ui';
import type {
  InsoleDesignState,
  InsoleLength,
  InsoleCup,
  PatientType,
  TopCoverMaterial,
  TopCoverLayer,
  FibreglassColor,
} from './prescriptionState';
import { CARD_IDS } from './cardIds';
import { isProphetName, PROPHET_NAME_WARNING } from './customNameRules';
import type { UnavailableLookup } from '../prescriptionLegality';

/**
 * InsoleDesignSection (R28 + polish).
 *
 * Subtype (from step 2) drives the material panel; patient weight (from
 * step 1) drives the default shell thickness. Defaults applied on mount:
 *   Top cover → EVA 2mm · DryFeet · Black  (always Adult; UCBL Kid auto-fill removed R34)
 *   Insole cup → Medium 12mm
 *   Shell thickness (text-box materials) → 2mm (≤85kg) or 2.5mm (>85kg)
 *   Shell thickness (carbon) → 1.4mm (≤85kg) or 2.1mm (>85kg)
 *
 * The user can override any default at any time.
 */

type Subtype =
  | 'pa12_insole'
  | 'tpu_insole'
  | 'polypropylene_insole'
  | 'carbon_insole'
  | 'eva_insole'
  | 'fibreglass_insole'
  | 'orthopaedic_sandal'
  | 'insoles_refurbishment';

interface Props {
  value: InsoleDesignState;
  onChange: (next: InsoleDesignState) => void;
  subtype: Subtype | null | undefined;
  /** From the patient step, used to default shell thickness by weight. */
  patientWeightKg?: number | null | undefined;
  /** Resolved patient full name (from the patient section above). Powers the
   *  "copy patient name" checkbox next to the custom-name field. */
  patientName?: string | null | undefined;
  readOnly?: boolean | undefined;
  defaultOpen?: boolean | undefined;
  open?: boolean | undefined;
  onOpenChange?: ((next: boolean) => void) | undefined;
  /** Show inline validation errors (set true after Continue is attempted). */
  showErrors?: boolean | undefined;
  /**
   * Insoles refurbishment trims the form to the fields a refurb job needs:
   * insole length + top cover (+ free-text remarks). Shell thickness,
   * insole cup, and custom-name fields are hidden, and the card title +
   * remarks label adjust to match the refurbishment context. Defaults
   * to false so every other subtype renders exactly as before.
   */
  refurbishmentMode?: boolean | undefined;
  /** Values this product does not offer, keyed by the checker's field key.
   *  Only `design_shell_thickness` is consumed here, and it is the field that
   *  most needed it: shell thickness is a PillRow, not a SelectSm, so an
   *  unavailable value used to leave every pill unselected with nothing red
   *  anywhere while the banner named it. See indexUnavailable in
   *  ../prescriptionLegality. */
  unavailable?: UnavailableLookup | undefined;
}

// ---- Constants ------------------------------------------------------------

export const LENGTH_OPTIONS: ReadonlyArray<{
  value: InsoleLength;
  label: string;
  src: string;
}> = [
  { value: 'full', label: 'Full length (4/4)', src: '/prescription/lengths/length-complete.png' },
  { value: 'sulcus', label: 'Rearfoot to sulcus (3/4)', src: '/prescription/lengths/length-sulcus.png' },
  { value: 'metatarsals', label: 'Rearfoot to metatarsals (2/4)', src: '/prescription/lengths/length-metatarsals.png' },
];

export const CUP_OPTIONS: ReadonlyArray<{ value: InsoleCup; label: string }> = [
  { value: 'very_low_4', label: 'Very low 4mm' },
  { value: 'low_8', label: 'Low 8mm' },
  { value: 'medium_12', label: 'Medium 12mm' },
  { value: 'high_20', label: 'High 20mm (kids only)' },
  { value: 'other', label: 'Other' },
];

export const EVA_TYPE_OPTIONS: readonly string[] = [
  'EVA Dual Density - Green / Black - 25° Sh.A / 25° Sh.A',
  'EVA Dual Density - Beige / Black - 25° Sh.A / 25° Sh.A',
  'EVA Dual Density - Brown / Brown - 30° Sh.A / 30° Sh.A',
  'EVA Tri Density - Green / Grey / Black - 25° / 25° / 45° Sh.A',
  'EVA Dual Density - Dark Blue / Black - 40° Sh.A / 40° Sh.A',
  'EVA Dual Density - Red / Black - 40° Sh.A / 40° Sh.A',
  'EVA Combi Dual Density - Grey / Dark Blue / Black - 30° / 50° Sh.A',
  'EVA Single Density - Grey - 60° Sh.A',
];

// Shell-thickness option sets moved to ./shellThicknessRules so the pure
// legality checker can read them without importing this component (and, through
// it, React and the eager supabase client). Re-exported here so every existing
// import site keeps working unchanged.
export {
  CARBON_THICKNESS,
  FIBREGLASS_BLUE_THICKNESS,
  FIBREGLASS_WHITE_THICKNESS,
} from './shellThicknessRules';
// A re-export does not bind the names in this module's scope, and they are
// used in the thickness pickers below.
import {
  CARBON_THICKNESS,
  FIBREGLASS_BLUE_THICKNESS,
  FIBREGLASS_WHITE_THICKNESS,
} from './shellThicknessRules';
// EVA base thickness rules live in their own pure module for the same reason
// (prescriptionFormDefaults, the legality checker and the exporters read
// them too).
import {
  evaThicknessProblem,
  formatEvaThicknessMm,
  isEvaThicknessDraft,
  parseEvaThicknessInput,
} from './evaThicknessRules';
import { EvaDensityGuideModal } from './EvaDensityGuideModal';
import { EVA_DENSITY_GUIDE_COPY } from './evaDensityGuide';

// Shell-thickness limits + weight-based defaults.
export const SHELL_THICKNESS_MIN = 1.8;
export const SHELL_THICKNESS_MAX = 6;
// R32 - structural shells (carbon / fibreglass) step up at 85kg; the
// moulded text-box shells (PA12 / TPU / Polypropylene) use a three-band
// scale per Rafael (80kg and 95kg breakpoints).
const WEIGHT_HEAVY_KG = 85;
const TEXT_BOX_SUBTYPES: ReadonlyArray<Subtype> = [
  'pa12_insole',
  'tpu_insole',
  'polypropylene_insole',
];

function defaultThicknessForTextBox(weightKg: number | null | undefined): number | undefined {
  if (weightKg == null) return undefined;
  if (weightKg < 80) return 2.0;
  if (weightKg <= 95) return 2.5;
  return 3.0;
}

function defaultThicknessForCarbon(weightKg: number | null | undefined): string | undefined {
  if (weightKg == null) return undefined;
  return weightKg > WEIGHT_HEAVY_KG ? '2.1mm' : '1.4mm';
}

function defaultThicknessForFibreglass(
  weightKg: number | null | undefined,
  color: FibreglassColor | undefined,
): string | undefined {
  if (weightKg == null) return undefined;
  // White (flex) only comes in 1.4mm, so there's nothing to branch on.
  if (color === 'white') return '1.4mm';
  // Blue (default): 1.5mm under 85kg, 2.2mm at/above.
  return weightKg > WEIGHT_HEAVY_KG ? '2.2mm' : '1.5mm';
}

/**
 * Top cover material list. R34 replaced 'Poron 3mm' with 'Poron' (the
 * hardness is now part of the Poron layer name, not the material itself).
 */
export const TOP_COVER_MATERIALS: ReadonlyArray<{ value: TopCoverMaterial; label: string }> = [
  { value: 'EVA 2mm', label: 'EVA 2mm' },
  { value: 'EVA 3mm', label: 'EVA 3mm' },
  { value: 'Poron', label: 'Poron' },
];

/** Remap pre-R34 'Poron 3mm' so legacy drafts hydrate against the new matrix. */
function normalizeMaterial(m: TopCoverMaterial | undefined): TopCoverMaterial {
  if (m === 'Poron 3mm') return 'Poron';
  return m ?? 'EVA 2mm';
}

/**
 * R34 — Layer options are now per-material:
 *   EVA 2mm / EVA 3mm → None, DryFeet, Leather
 *   Poron             → Yellow (7° Sh.A) & DryFeet  /  Red (12° Sh.A) & DryFeet
 *                       (the hardness picker is folded into the layer label so
 *                        the lab knows both shore + cover from one selection)
 */
export const TOP_COVER_LAYERS_BY_MATERIAL: Record<
  TopCoverMaterial,
  ReadonlyArray<{ value: TopCoverLayer; label: string }>
> = {
  'EVA 2mm': [
    { value: 'None', label: 'None' },
    { value: 'DryFeet', label: 'DryFeet' },
    { value: 'Leather', label: 'Leather' },
  ],
  'EVA 3mm': [
    { value: 'None', label: 'None' },
    { value: 'DryFeet', label: 'DryFeet' },
    { value: 'Leather', label: 'Leather' },
  ],
  Poron: [
    { value: 'Yellow 7 DryFeet', label: 'Yellow (7° Sh.A) & DryFeet' },
    { value: 'Red 12 DryFeet', label: 'Red (12° Sh.A) & DryFeet' },
  ],
  // Legacy placeholder, kept so the Record type stays exhaustive. Not rendered.
  'Poron 3mm': [],
};

/**
 * R34 — Color options are per (material, layer). Drives which color tiles
 * render and which one auto-seeds when the upstream choice changes.
 */
export const TOP_COVER_COLORS_BY: Record<
  TopCoverMaterial,
  Record<string, readonly string[]>
> = {
  'EVA 2mm': {
    None: ['Black', 'Anthracite', 'Beige'],
    DryFeet: ['Black', 'Anthracite', 'Dark Blue'],
    Leather: ['Black'],
  },
  'EVA 3mm': {
    None: ['Black', 'Beige', 'Orange', 'Cobalt Blue'],
    DryFeet: ['Black', 'Anthracite', 'Beige', 'Dark Blue', 'Brown'],
    Leather: ['Black'],
  },
  Poron: {
    'Yellow 7 DryFeet': ['Black'],
    'Red 12 DryFeet': ['Black'],
  },
  'Poron 3mm': {},
};

function layersFor(material: TopCoverMaterial) {
  return TOP_COVER_LAYERS_BY_MATERIAL[normalizeMaterial(material)] ?? [];
}

function colorsFor(material: TopCoverMaterial, layer: TopCoverLayer | undefined): readonly string[] {
  const m = normalizeMaterial(material);
  if (!layer) return [];
  return TOP_COVER_COLORS_BY[m]?.[layer] ?? [];
}

/**
 * Leather palette for shells the Spanish supplier produces (carbon fibre,
 * fibreglass, EVA): the full seven-colour range, in this exact order.
 * PA12/TPU are made locally in Dubai, where Leather is stocked in Black
 * only, so they keep the single Black option via the R38 branch below.
 * Colour is configuration-only (configuration.deep.design.top_cover_color):
 * it never feeds mapCustomLineToSku, priceForSku or any Zoho line, so no
 * catalog_skus row is needed behind these.
 */
export const LEATHER_EU_SHELL_COLORS: readonly string[] = [
  'Black',
  'Red',
  'Dark Blue',
  'Brown',
  'Anthracite',
  'Aqua Blue',
  'Beige',
];

const LEATHER_FULL_PALETTE_SUBTYPES: ReadonlySet<Subtype> = new Set([
  'carbon_insole',
  'fibreglass_insole',
  'eva_insole',
] as const);

/**
 * R38 — Shell-aware top-cover colours. For PA12 and TPU shells ONLY, the EVA
 * cover palette is restricted per layer:
 *   EVA 2mm + DryFeet → Black, Brown   |  EVA 3mm + DryFeet → Black, Beige
 *   EVA 2mm/3mm + None/Leather → Black  |  Poron → Black (already Black-only)
 * Leather on carbon fibre / fibreglass / EVA shells offers the full
 * seven-colour Spanish-supplier range (LEATHER_EU_SHELL_COLORS). Every other
 * (shell, layer) combination keeps the standard (material, layer) palette
 * above — deliberately including polypropylene, sandal, refurbishment and
 * the null-subtype legacy renders, which stay Black-only for Leather.
 *
 * Exported for the tests that pin the per-shell palettes; the paper forms
 * print from the base TOP_COVER_COLORS_BY matrix and intentionally do NOT
 * see these shell-conditional rules.
 */
export function coverColorsFor(
  subtype: Subtype | null | undefined,
  material: TopCoverMaterial,
  layer: TopCoverLayer | undefined,
): readonly string[] {
  if (subtype === 'pa12_insole' || subtype === 'tpu_insole') {
    const m = normalizeMaterial(material);
    if (m === 'EVA 2mm') return layer === 'DryFeet' ? ['Black', 'Brown'] : ['Black'];
    if (m === 'EVA 3mm') return layer === 'DryFeet' ? ['Black', 'Beige'] : ['Black'];
    return ['Black']; // Poron (and any legacy cover) stays Black-only
  }
  if (layer === 'Leather' && subtype && LEATHER_FULL_PALETTE_SUBTYPES.has(subtype)) {
    return LEATHER_EU_SHELL_COLORS;
  }
  return colorsFor(material, layer);
}

/**
 * Event-time reset for an impossible top-cover colour after a SHELL change
 * (e.g. Leather Red under carbon fibre, DryFeet Brown on EVA 3mm, or None
 * Anthracite on EVA 2mm, with the shell switched to PA12). Returns the
 * colour to store, or null when the stored state needs no change. Covers
 * the Leather, DryFeet AND None layers (DryFeet added 2026-09-08, None
 * closing the class the same day: identical silent bug each time, the
 * render fallback paints a legal colour while the STORED value is what
 * serialises). The Poron layers deliberately have NO branch: they are
 * Black-only under every shell, so no illegal state can exist there.
 * Called from the subtype onChange handlers (wizard + edit dialog), never
 * from render or an effect, following the applySoleOptions precedent.
 */
export function sanitizeTopCoverColorForSubtype(
  subtype: Subtype | null | undefined,
  design:
    | {
        top_cover_material?: TopCoverMaterial | undefined;
        top_cover_layer?: TopCoverLayer | undefined;
        top_cover_color?: string | undefined;
      }
    | undefined,
): string | null {
  if (!design) return null;
  const layer = design.top_cover_layer;
  if (layer !== 'Leather' && layer !== 'DryFeet' && layer !== 'None') return null;
  if (!design.top_cover_material) return null;
  const colors = coverColorsFor(subtype, design.top_cover_material, layer);
  if (design.top_cover_color && colors.includes(design.top_cover_color)) return null;
  return colors[0] ?? 'Black';
}

/**
 * Re-pick a valid (material, layer, color) triple after any upstream change.
 * If the current layer is still valid for the new material, keep it; same
 * for color. Falls back to the first allowed option otherwise so the user
 * never lands in an unselectable state.
 */
function cascade(
  subtype: Subtype | null | undefined,
  material: TopCoverMaterial,
  currentLayer: TopCoverLayer | undefined,
  currentColor: string | undefined,
): { layer: TopCoverLayer; color: string } {
  const m = normalizeMaterial(material);
  const layers = layersFor(m);
  const firstLayer = layers[0]?.value ?? 'None';
  const layer =
    currentLayer && layers.some((l) => l.value === currentLayer) ? currentLayer : firstLayer;
  const colors = coverColorsFor(subtype, m, layer);
  const color =
    currentColor && colors.includes(currentColor) ? currentColor : (colors[0] ?? 'Black');
  return { layer, color };
}

/**
 * Kid branch — restored R34. Kids get a fixed material + layer pair
 * (EVA 3mm · None) and a marbled colour palette. The UCBL auto-fill that
 * used to push users into Kid based on intended_use is gone; the only
 * way into Kid mode now is an explicit click on the Patient-type tile.
 */
const KID_MATERIAL: TopCoverMaterial = 'EVA 3mm';
const KID_LAYER: TopCoverLayer = 'None';
export const KID_COLORS: ReadonlyArray<{ value: 'pink' | 'blue' | 'red'; label: string }> = [
  { value: 'pink', label: 'Marbled pink' },
  { value: 'blue', label: 'Marbled blue' },
  { value: 'red', label: 'Marbled red' },
];

// ---- Component -----------------------------------------------------------

export function InsoleDesignSection({
  value,
  onChange,
  subtype,
  patientWeightKg,
  patientName,
  readOnly,
  defaultOpen = true,
  open,
  onOpenChange,
  showErrors,
  refurbishmentMode = false,
  unavailable,
}: Props) {
  const patch = React.useCallback(
    (p: Partial<InsoleDesignState>) => onChange({ ...value, ...p }),
    [onChange, value],
  );

  // "Copy patient name" is a one-shot action driven by a checkbox; its checked
  // state is cosmetic (the operator freely overwrites the field afterwards).
  const [copyPatientName, setCopyPatientName] = React.useState(false);
  // Whole-word, case-insensitive match anywhere in the custom name, regardless
  // of how the value got there (typed or copied).
  const prophetNameFlagged = isProphetName(value.custom_name);

  // R28 - apply top-cover + cup defaults once on mount. Won't overwrite
  // anything the user has already touched (each line guards on the
  // corresponding field being unset).
  const initialized = React.useRef(false);
  React.useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    const updates: Partial<InsoleDesignState> = {};
    if (!value.top_cover_patient_type) updates.top_cover_patient_type = 'adult';
    if (!value.top_cover_material) updates.top_cover_material = 'EVA 2mm';
    if (!value.top_cover_layer) updates.top_cover_layer = 'DryFeet';
    if (!value.top_cover_color) updates.top_cover_color = 'Black';
    if (!value.insole_cup) updates.insole_cup = 'medium_12';
    // R32 - length defaults to full so the required field is satisfied
    // out of the box; the podiatrist trims it down if needed.
    if (!value.insole_length) updates.insole_length = 'full';
    if (Object.keys(updates).length > 0) {
      onChange({ ...value, ...updates });
    }

    // Mount-only initialization guarded by initialized.current ref. Including
    // `value` or `onChange` would re-run this on every parent render and
    // potentially clobber user input or loop (onChange updates value).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // R28 - apply weight-based default for shell thickness when subtype or
  // weight changes (but only if the user hasn't typed/picked anything).
  React.useEffect(() => {
    if (!subtype || readOnly) return;
    if (TEXT_BOX_SUBTYPES.includes(subtype)) {
      if (value.shell_thickness_mm == null) {
        const def = defaultThicknessForTextBox(patientWeightKg);
        if (def != null) onChange({ ...value, shell_thickness_mm: def });
      }
    } else if (subtype === 'carbon_insole') {
      if (!value.shell_thickness) {
        const def = defaultThicknessForCarbon(patientWeightKg);
        if (def) onChange({ ...value, shell_thickness: def });
      }
    } else if (subtype === 'fibreglass_insole') {
      if (!value.shell_thickness) {
        const def = defaultThicknessForFibreglass(patientWeightKg, value.fibreglass_color);
        if (def) onChange({ ...value, shell_thickness: def });
      }
    }

    // Intentionally trigger only on subtype/weight/fibreglass_color changes.
    // Adding `value` or `onChange` would re-fire on every keystroke (onChange
    // mutates value, which would loop), and `readOnly` is a stable prop checked
    // inside the effect as an early bail. Each branch already guards against
    // overwriting user input via the `!value.shell_thickness*` checks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subtype, patientWeightKg, value.fibreglass_color]);

  // R34: UCBL Kid AUTO-fill removed; Kid PATIENT TYPE restored as an
  // explicit user choice. Kid forces EVA 3mm + Layer None + marbled
  // colour palette (no DryFeet / Leather for kids). The 'ucbl_kids'
  // enum on the patient form is now a no-op label; the only way into
  // Kid mode is an explicit tile click.
  const patientType: PatientType = value.top_cover_patient_type ?? 'adult';
  const tcMaterial = normalizeMaterial(value.top_cover_material);
  const layers = layersFor(tcMaterial);
  const tcLayer = (value.top_cover_layer && layers.some((l) => l.value === value.top_cover_layer)
    ? value.top_cover_layer
    : (layers[0]?.value ?? 'None')) as TopCoverLayer;
  const availableColors = coverColorsFor(subtype, tcMaterial, tcLayer);
  const tcColor =
    value.top_cover_color && availableColors.includes(value.top_cover_color)
      ? value.top_cover_color
      : (availableColors[0] ?? 'Black');
  const cup = value.insole_cup ?? 'medium_12';

  const cupOtherMissing =
    cup === 'other' && !(value.insole_cup_other ?? '').trim();

  const isControlled = open !== undefined && onOpenChange !== undefined;

  return (
    <CollapsibleCard
      title={refurbishmentMode ? 'Insole refurbishment' : 'Insole design'}
      subtitle={
        refurbishmentMode
          ? 'Length, top cover, refurbishment remarks'
          : 'Material specs, length, cup, top cover, custom name'
      }
      {...(isControlled
        ? { open: open!, onOpenChange: onOpenChange! }
        : { defaultOpen })}
    >
      <div className="space-y-4">
        {/* Product family (read-only, from step 3) + material-specific panel.
            Refurbishment hides the shell-thickness panel entirely - the
            existing shell is being re-covered, not re-fabricated - so the
            Product family chip rides alone in the row. */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <FormField label="Product family">
            <div className="h-8 flex items-center px-3 rounded-md border border-border-subtle bg-sand-50 text-sm text-text-secondary">
              {subtype ? subtypeDisplayName(subtype) : '—'}
            </div>
          </FormField>
          {!refurbishmentMode ? (
            <div className="md:col-span-2">
              <MaterialPanel
                subtype={subtype}
                value={value}
                onChange={patch}
                readOnly={readOnly}
                showErrors={showErrors}
                unavailable={unavailable}
              />
            </div>
          ) : null}
        </div>

        {/* Length + Cup row (2:1). Refurbishment hides the cup field -
            the existing shell already has its cup geometry - so length
            takes the full row. */}
        <div
          className={cn(
            'grid grid-cols-1 gap-4',
            !refurbishmentMode && 'md:grid-cols-[2fr_1fr]',
          )}
        >
          <FormField label="Insole length (including top cover)" required>
            <div className="grid grid-cols-3 gap-2">
              {LENGTH_OPTIONS.map((opt) => {
                // Mirror the cup fallback so the default tile lights up on
                // first paint (before the mount-init effect commits) and on
                // any persisted draft that predates the length default.
                const selected = (value.insole_length ?? 'full') === opt.value;
                return (
                  <button
                    type="button"
                    key={opt.value}
                    onClick={() => !readOnly && patch({ insole_length: opt.value })}
                    disabled={readOnly}
                    className={cn(
                      'p-1.5 rounded-md border-2 transition flex flex-col items-center',
                      selected
                        ? 'border-brand-500 bg-brand-50'
                        : 'border-border-subtle hover:border-brand-300 bg-surface-elevated',
                    )}
                  >
                    <img
                      src={opt.src}
                      alt=""
                      className="h-14 w-auto object-contain"
                      loading="lazy"
                    />
                    <span className="text-[11px] text-text-secondary mt-1 leading-tight text-center">
                      {opt.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </FormField>

          {!refurbishmentMode ? (
            <FormField
              label="Insole cup"
              required
              error={showErrors && cupOtherMissing ? 'Required when "Other" is selected' : undefined}
            >
              <select
                value={cup}
                onChange={(e) => {
                  const next = e.target.value as InsoleCup;
                  if (next === 'other') {
                    patch({ insole_cup: next });
                  } else {
                    patch({ insole_cup: next, insole_cup_other: undefined });
                  }
                }}
                disabled={readOnly}
                className="w-full h-8 rounded-md border border-border-default bg-surface-elevated px-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              >
                {CUP_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              {cup === 'other' ? (
                <Input
                  className={cn(
                    'mt-1.5 h-8',
                    showErrors && cupOtherMissing && 'border-error-500',
                  )}
                  placeholder="e.g. 15mm"
                  value={value.insole_cup_other ?? ''}
                  onChange={(e) => patch({ insole_cup_other: e.target.value })}
                  disabled={readOnly}
                  required
                />
              ) : null}
            </FormField>
          ) : null}
        </div>

        {/* Top cover - shown for every subtype (PA12 / TPU included per
            Rafael's review: the 3D-printed shells take a top cover too).
            Wrapped in a scrollable anchor div so the prescription assistant
            chips can jump to it. */}
        <div id={CARD_IDS.topCover} className="scroll-mt-32">
        <FormField label="Top cover" required>
            {/* R34 - 4-col grid: Patient type → Material → Layer → Color.
                Adult uses the (material, layer) matrix; Kid is a fixed
                EVA 3mm · None pair with a marbled color palette. */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {/* Patient type */}
              <ColStack title="Patient type">
                {(['adult', 'kid'] as const).map((t) => (
                  <Cell
                    key={t}
                    label={t === 'adult' ? 'Adult' : 'Kid'}
                    selected={patientType === t}
                    onClick={() => {
                      if (t === 'kid') {
                        // Snap to the kid pair; clear the adult colour so
                        // the kid palette renders fresh.
                        patch({
                          top_cover_patient_type: 'kid',
                          top_cover_material: KID_MATERIAL,
                          top_cover_layer: KID_LAYER,
                          top_cover_color: undefined,
                        });
                      } else {
                        // Back to adult — re-seed defaults if they were
                        // cleared while in kid mode.
                        patch({
                          top_cover_patient_type: 'adult',
                          top_cover_material: value.top_cover_material ?? 'EVA 2mm',
                          top_cover_layer: value.top_cover_layer ?? 'DryFeet',
                          top_cover_color: value.top_cover_color ?? 'Black',
                          top_cover_kid_color: undefined,
                        });
                      }
                    }}
                    disabled={readOnly}
                  />
                ))}
              </ColStack>

              {/* Material — locked to EVA 3mm in kid mode. */}
              <ColStack title="Material">
                {TOP_COVER_MATERIALS.map((m) => (
                  <Cell
                    key={m.value}
                    label={m.label}
                    selected={tcMaterial === m.value}
                    onClick={() => {
                      const { layer, color } = cascade(subtype, m.value, value.top_cover_layer, value.top_cover_color);
                      patch({
                        top_cover_material: m.value,
                        top_cover_layer: layer,
                        top_cover_color: color,
                      });
                    }}
                    disabled={readOnly || patientType === 'kid'}
                  />
                ))}
              </ColStack>

              {/* Layer — list narrows by material; in kid mode it
                  collapses to a single "None" cell so the dimension is
                  unambiguous (color is the only remaining axis). */}
              <ColStack title="Layer">
                {(patientType === 'kid'
                  ? layers.filter((l) => l.value === 'None')
                  : layers
                ).map((l) => (
                  <Cell
                    key={l.value}
                    label={l.label}
                    selected={tcLayer === l.value}
                    onClick={() => {
                      const colors = coverColorsFor(subtype, tcMaterial, l.value);
                      const color =
                        value.top_cover_color && colors.includes(value.top_cover_color)
                          ? value.top_cover_color
                          : (colors[0] ?? 'Black');
                      patch({ top_cover_layer: l.value, top_cover_color: color });
                    }}
                    disabled={readOnly}
                  />
                ))}
              </ColStack>

              {/* Color — Adult: (material, layer) matrix. Kid: marbled palette. */}
              <ColStack title="Color">
                {patientType === 'kid'
                  ? KID_COLORS.map((c) => (
                      <Cell
                        key={c.value}
                        label={c.label}
                        selected={value.top_cover_kid_color === c.value}
                        onClick={() => patch({ top_cover_kid_color: c.value })}
                        disabled={readOnly}
                      />
                    ))
                  : availableColors.map((color) => (
                      <Cell
                        key={color}
                        label={color}
                        selected={tcColor === color}
                        onClick={() => patch({ top_cover_color: color })}
                        disabled={readOnly}
                      />
                    ))}
              </ColStack>
            </div>
            <p className="text-[11px] text-text-muted mt-2">
              {patientType === 'kid'
                ? 'Kid top cover: EVA 3mm + marbled colour. Layer + material locked for kid orthotics.'
                : tcMaterial === 'Poron'
                  ? 'Poron pressure-redistribution layer with integrated DryFeet finish. Hardness picked via the layer (yellow = soft 7° Sh.A, red = firmer 12° Sh.A).'
                  : tcLayer === 'Leather'
                    ? 'Premium leather top cover. Adds longevity + a refined feel. The material is a smooth microfibre with a leather-like appearance, and it performs particularly well in hot climates because it helps absorb perspiration.'
                    : tcLayer === 'DryFeet'
                      ? 'Includes a microperforated DryFeet microfiber layer (0.8mm) for breathability + dry-feet feel.'
                      : 'No top-cover layer selected. Base EVA is exposed.'}
            </p>
        </FormField>
        </div>

        {/* Custom name + design remarks. Refurbishment hides custom-name
            (no engraving on a re-covered shell) and renames the remarks
            field to capture additional refurbishment scope. */}
        <div
          className={cn(
            'grid grid-cols-1 gap-4',
            !refurbishmentMode && 'md:grid-cols-2',
          )}
        >
          {!refurbishmentMode ? (
            <FormField label="Custom name">
              <Input
                placeholder="Patient name to print on insole (optional)"
                value={value.custom_name ?? ''}
                onChange={(e) => patch({ custom_name: e.target.value })}
                disabled={readOnly}
                className="h-8"
                data-testid="custom-name-input"
              />
              {/* Copy patient name — one-shot copy from the patient section
                  above. Writes nothing when the patient name is empty; the
                  operator can freely overwrite the field afterwards. */}
              {!readOnly ? (
                <label className="mt-1.5 inline-flex items-center gap-2 text-xs text-text-secondary cursor-pointer select-none">
                  <input
                    type="checkbox"
                    data-testid="custom-name-copy-patient"
                    checked={copyPatientName}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setCopyPatientName(checked);
                      const name = (patientName ?? '').trim();
                      if (checked && name) patch({ custom_name: name });
                    }}
                    className="h-3.5 w-3.5 rounded border-border-default text-brand-600 focus:ring-brand-500"
                  />
                  Copy patient name
                </label>
              ) : null}
              {/* Informative (non-blocking) warning when a name of the Prophet
                  is present, regardless of how it was entered. */}
              {prophetNameFlagged ? (
                <p
                  role="alert"
                  data-testid="custom-name-prophet-warning"
                  className="mt-1.5 text-xs font-medium text-error-600"
                >
                  {PROPHET_NAME_WARNING}
                </p>
              ) : null}
            </FormField>
          ) : null}
          <FormField
            label={
              refurbishmentMode
                ? 'Refurbishment remarks (any additional item to be included)'
                : 'Design remarks'
            }
          >
            <Textarea
              placeholder={
                refurbishmentMode
                  ? 'List anything else needed for the refurbishment…'
                  : 'Any extra design details for the lab…'
              }
              value={value.design_remarks ?? ''}
              onChange={(e) => patch({ design_remarks: e.target.value })}
              disabled={readOnly}
              className="min-h-[72px] text-sm"
            />
          </FormField>
        </div>
      </div>
    </CollapsibleCard>
  );
}

// ---- Helpers --------------------------------------------------------------

export const SUBTYPE_DISPLAY: Record<Subtype, string> = {
  pa12_insole: 'PA12 insole',
  tpu_insole: 'TPU insole',
  polypropylene_insole: 'Polypropylene insole',
  carbon_insole: 'Carbon fibre insole',
  eva_insole: 'EVA insole',
  fibreglass_insole: 'Fibreglass insole',
  orthopaedic_sandal: 'Orthopaedic sandal',
  insoles_refurbishment: 'Insoles refurbishment',
};

function subtypeDisplayName(s: Subtype): string {
  return SUBTYPE_DISPLAY[s] ?? s;
}

// ---- Material-specific panel --------------------------------------------

function MaterialPanel({
  subtype,
  value,
  onChange,
  readOnly,
  showErrors,
  unavailable,
}: {
  subtype: Subtype | null | undefined;
  value: InsoleDesignState;
  onChange: (patch: Partial<InsoleDesignState>) => void;
  readOnly?: boolean | undefined;
  showErrors?: boolean | undefined;
  unavailable?: UnavailableLookup | undefined;
}) {
  const { t } = useTranslation('orders');
  // EVA density guide (reference pages), opened from a link under the EVA
  // controls. Hooks stay above the early returns; only the EVA branch reads
  // them.
  const [guideOpen, setGuideOpen] = React.useState(false);
  // EVA thickness is typed into a text input, so the panel keeps a DRAFT
  // string beside the stored number: "2." and "2," must survive a render
  // (they parse to 2, which is what the form then holds) or a decimal could
  // never be typed one key at a time. The stored number stays the source of
  // truth: the draft is re-derived from it whenever it changes from outside
  // the input (hydration, a reuse prefill, a preset, a subtype switch), and
  // left alone while its own parse still matches, which is the typing case.
  const evaThickness = value.eva_thickness_mm;
  const [evaThicknessDraft, setEvaThicknessDraft] = React.useState(() =>
    evaThickness != null ? formatEvaThicknessMm(evaThickness) : '',
  );
  React.useEffect(() => {
    setEvaThicknessDraft((draft) => {
      if (parseEvaThicknessInput(draft) === evaThickness) return draft;
      return evaThickness != null ? formatEvaThicknessMm(evaThickness) : '';
    });
  }, [evaThickness]);
  // The one shell field in this panel the legality checker reports on.
  const badThickness = unavailable?.['design_shell_thickness'];
  if (!subtype) return null;

  if (
    subtype === 'pa12_insole' ||
    subtype === 'tpu_insole' ||
    subtype === 'polypropylene_insole'
  ) {
    const thickness = value.shell_thickness_mm;
    const isInvalid =
      thickness != null &&
      (thickness < SHELL_THICKNESS_MIN || thickness > SHELL_THICKNESS_MAX);

    return (
      <FormField
        label="Shell thickness (mm)"
        required
        error={
          showErrors && isInvalid
            ? `Must be between ${SHELL_THICKNESS_MIN} and ${SHELL_THICKNESS_MAX}mm`
            : undefined
        }
      >
        <Input
          type="number"
          min={SHELL_THICKNESS_MIN}
          max={SHELL_THICKNESS_MAX}
          step={0.1}
          placeholder={`${SHELL_THICKNESS_MIN}–${SHELL_THICKNESS_MAX}mm`}
          value={thickness ?? ''}
          onChange={(e) => {
            const n = parseFloat(e.target.value);
            onChange({ shell_thickness_mm: Number.isFinite(n) ? n : undefined });
          }}
          onBlur={(e) => {
            // Clamp value on blur so the persisted state is always in range.
            const n = parseFloat(e.target.value);
            if (!Number.isFinite(n)) return;
            const clamped = Math.min(SHELL_THICKNESS_MAX, Math.max(SHELL_THICKNESS_MIN, n));
            if (clamped !== n) onChange({ shell_thickness_mm: clamped });
          }}
          disabled={readOnly}
          className={cn('h-8', showErrors && isInvalid && 'border-error-500')}
        />
      </FormField>
    );
  }

  if (subtype === 'carbon_insole') {
    return (
      <FormField label="Shell thickness" required>
        <PillRow
          options={CARBON_THICKNESS.map((t) => ({ value: t, label: t }))}
          value={value.shell_thickness}
          onChange={(v) => onChange({ shell_thickness: v })}
          disabled={readOnly}
          unavailableLabel={badThickness?.valueLabel}
        />
      </FormField>
    );
  }

  if (subtype === 'fibreglass_insole') {
    const opts =
      value.fibreglass_color === 'white'
        ? FIBREGLASS_WHITE_THICKNESS
        : FIBREGLASS_BLUE_THICKNESS;
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField label="Fibreglass color">
          <PillRow
            options={[
              { value: 'blue', label: 'Blue' },
              { value: 'white', label: 'White (flex)' },
            ]}
            value={value.fibreglass_color}
            onChange={(v) => onChange({ fibreglass_color: v as FibreglassColor })}
            disabled={readOnly}
          />
        </FormField>
        <FormField label="Shell thickness" required>
          <PillRow
            options={opts.map((t) => ({ value: t, label: t }))}
            value={value.shell_thickness}
            onChange={(v) => onChange({ shell_thickness: v })}
            disabled={readOnly}
            unavailableLabel={badThickness?.valueLabel}
          />
        </FormField>
      </div>
    );
  }

  if (subtype === 'eva_insole') {
    // Thickness is optional: EVA lines written before the field existed have
    // no value and must stay saveable, so an empty input reads "Not
    // specified" and stores nothing. The rule (2 to 6 mm, at most one
    // decimal) is shown under the input the moment it is broken, NOT gated
    // on showErrors: the podiatrist should see it as they type, and a stored
    // value off the rule (a 7 from before it) is never hidden or altered,
    // only shown as it is until they correct it. The wizard's submit and the
    // edit dialog's save both block on this same evaThicknessProblem.
    //
    // Not in read-only viewers, for the reason PrescriptionForm gives for
    // its banner: a locked delivered order is not a form to fix, and a red
    // rule under a disabled input is an alarm nobody can act on. The value
    // itself stays visible as stored.
    const evaProblem = readOnly ? null : evaThicknessProblem(evaThickness);
    return (
      <div className="space-y-2">
        <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr] gap-4">
          <FormField label="EVA type" required>
            <select
              value={value.eva_type ?? ''}
              onChange={(e) => onChange({ eva_type: e.target.value || undefined })}
              disabled={readOnly}
              className="w-full h-8 rounded-md border border-border-default bg-surface-elevated px-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            >
              <option value="">Select EVA type…</option>
              {EVA_TYPE_OPTIONS.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label="EVA thickness (mm)" error={evaProblem}>
            <Input
              type="text"
              inputMode="decimal"
              placeholder="Not specified"
              value={evaThicknessDraft}
              onChange={(e) => {
                const raw = e.target.value;
                // A letter or a second separator is simply not entered: the
                // draft, and so the controlled input, keeps its last value.
                if (!isEvaThicknessDraft(raw)) return;
                setEvaThicknessDraft(raw);
                // Stored as a NUMBER exactly as typed (comma read as the
                // decimal point); no rounding, no clamping.
                onChange({ eva_thickness_mm: parseEvaThicknessInput(raw) });
              }}
              disabled={readOnly}
              data-testid="design-eva-thickness-mm"
              className={cn('h-8', evaProblem && 'border-error-500')}
            />
          </FormField>
        </div>
        {/* Reference material, so it stays visible in read-only mode: the
            viewer can still look up what a stored EVA type means. Rendered
            only inside this branch; no other shell has density pages. */}
        <button
          type="button"
          data-testid="eva-density-help-link"
          onClick={() => setGuideOpen(true)}
          className="text-sm text-brand-700 underline underline-offset-2 hover:text-brand-800 text-start"
        >
          {t('eva_density_help_link', {
            defaultValue: EVA_DENSITY_GUIDE_COPY.eva_density_help_link,
          })}
        </button>
        <EvaDensityGuideModal open={guideOpen} onOpenChange={setGuideOpen} />
      </div>
    );
  }

  return null;
}

// ---- Small helpers -------------------------------------------------------

function ColStack({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <p className="text-[11px] uppercase tracking-wide text-text-muted font-medium">{title}</p>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function Cell({
  label,
  selected,
  onClick,
  disabled,
}: {
  label: React.ReactNode;
  selected: boolean;
  onClick?: (() => void) | undefined;
  disabled?: boolean | undefined;
}) {
  return (
    <button
      type="button"
      onClick={() => !disabled && onClick?.()}
      disabled={disabled}
      className={cn(
        'w-full text-center text-[11px] max-sm:text-xs rounded border px-2 py-1.5 max-sm:py-2 transition',
        selected
          ? 'border-brand-500 bg-brand-50 text-brand-700 font-medium'
          : 'border-border-subtle text-text-primary hover:border-brand-300',
        disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
      )}
    >
      {label}
    </button>
  );
}

function PillRow<T extends string>({
  options,
  value,
  onChange,
  disabled,
  unavailableLabel,
}: {
  options: ReadonlyArray<{ value: T; label: string }>;
  value: T | undefined;
  onChange: (v: T) => void;
  disabled?: boolean | undefined;
  /** Human label for a value absent from `options`, when the caller knows one.
   *  Without it the raw stored value is shown, which for thickness IS the
   *  display label, so the fallback is not a degradation here. */
  unavailableLabel?: string | undefined;
}) {
  // A value this product does not offer.
  //
  // Mirrors the identical check in SelectSm, and for the same reason. A pill
  // row whose value matches no pill renders every pill UNSELECTED, which is
  // pixel-identical to "nothing has been chosen yet" while the value stays
  // live in state and prints on the lab sheet. The red banner named the field
  // and the operator then looked at a control with nothing wrong with it.
  //
  // Local and prop-free like SelectSm's, so it catches an unavailable value
  // from ANY source: a copied prescription, a subtype changed after the fact,
  // or legacy data. 57 live lines carry the retired carbon 2.0mm.
  //
  // `value == null` covers BOTH undefined and a stored null. A null read off a
  // legacy line means "nothing chosen", not "an unavailable choice", and
  // reporting it would paint the row red on a field the checker regards as
  // simply unset, so no banner and no submit block would explain it.
  //
  // `disabled` is the read-only viewer. Suppressed there for the same reason
  // the banner is (see PrescriptionForm): a delivered order in a drawer headed
  // "Order locked for editing" is not a form to fix, and 57 live lines carry
  // the retired carbon 2.0mm, so every one would open with a red alarm and no
  // available action. Review caught this; the first draft alarmed on all 57.
  const unavailable =
    !disabled &&
    value != null &&
    value !== ('' as T) &&
    !options.some((o) => o.value === value);

  return (
    <div className="inline-flex flex-wrap gap-1">
      {/* Rendered FIRST so it reads before the choices, and non-interactive:
          the point is to show what is there, not to offer it again. Wording
          matches SelectSm's for the identical condition, so one situation does
          not read as two different problems. */}
      {unavailable ? (
        <span
          role="status"
          className="px-2.5 py-1 rounded-md border border-error-500 bg-error-50 text-xs font-medium text-error-700"
        >
          {`${unavailableLabel ?? String(value)} (not available for this product)`}
        </span>
      ) : null}
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => !disabled && onChange(o.value)}
          disabled={disabled}
          className={cn(
            'px-2.5 py-1 rounded-md border text-xs transition',
            value === o.value
              ? 'border-brand-500 bg-brand-50 text-brand-700 font-medium'
              : 'border-border-subtle hover:border-brand-300',
            disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
